import React, { useState } from "react";
import { Select } from "antd";
import { Patient } from "../../interfaces/Patient";
import { VisitMedicine, Instructions } from "../../interfaces/Visit";
import { validatePhone } from "../../utils/Validation";

type EditablePrescriptionProps = {
  patient: Patient;
  medicines: Array<VisitMedicine>;
  symptoms: Array<string>;
  finalDiagnosis?: string | null;
  chiefComplaint?: string | null;
  advice?: string;
  mode?: "create" | "edit";

  onSave: (data: {
    patient: Patient;
    medicines: Array<VisitMedicine>;
    symptoms: Array<string>;
    finalDiagnosis?: string | null;

    chiefComplaint?: string | null;
    advice?: string;
  }) => void;
  onCancel: () => void;
};

const EditablePrescription: React.FC<EditablePrescriptionProps> = ({
  patient: initialPatient,
  medicines: initialMedicines,
  symptoms: initialSymptoms,
  finalDiagnosis: initialFinalDiagnosis,
  chiefComplaint: initialChiefComplaint,
  advice: initialAdvice,
  mode = "create",
  onSave,
  onCancel,
}) => {
  console.log("i am herer", initialChiefComplaint, initialAdvice);
  const [patient, setPatient] = useState<Patient>(initialPatient);
  const [medicines, setMedicines] = useState<Array<VisitMedicine>>(
    initialMedicines || []
  );
  const [symptoms, setSymptoms] = useState<Array<string>>(
    initialSymptoms || []
  );
  const [finalDiagnosis, setFinalDiagnosis] = useState<string | null>(
    initialFinalDiagnosis || null
  );

  const [chiefComplaint, setChiefComplaint] = useState<string | null>(
    initialChiefComplaint || null
  );
  const [advice, setAdvice] = useState<string>(initialAdvice || "");
  const [newSymptom, setNewSymptom] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");

  const forms = [
    { name: "Tablet", value: "tablet" },
    { name: "Capsule", value: "capsule" },
    { name: "Syrup", value: "syrup" },
    { name: "Drops", value: "drops" },
    { name: "Creame", value: "creame" },
    { name: "Lotion", value: "lotion" },
    { name: "gel", value: "gel" },
    { name: "Powder", value: "powder" },
    { name: "Soap", value: "soap" },
    { name: "Inhaler", value: "inhaler" },
  ];

  const mealTypes = [
    { name: "Before Meal", value: "before_meal" },
    { name: "After Meal", value: "after_meal" },
  ];

  const durations = [
    { name: "Days", value: "days" },
    { name: "Weeks", value: "weeks" },
    { name: "Months", value: "months" },
  ];

  const frequencies = [
    { name: "Daily", value: "daily" },
    { name: "Weekly", value: "weekly" },
    { name: "Alternate Days", value: "alternate days" },
  ];

  const genders = [
    { id: "male", title: "Male" },
    { id: "female", title: "Female" },
    { id: "other", title: "Other" },
  ];

  const updatePatientField = (field: keyof Patient, value: any) => {
    setPatient({ ...patient, [field]: value });

    // Validate phone number
    if (field === "phone") {
      if (value === "") {
        setPhoneError("Phone number is required!");
      } else if (!validatePhone(value)) {
        setPhoneError("Phone number should be 10 digits");
      } else {
        setPhoneError("");
      }
    }
  };

  const updateMedicine = (
    index: number,
    field: keyof Instructions | "name",
    value: any
  ) => {
    try {
      const updatedMedicines = [...medicines];
      if (index < 0 || index >= updatedMedicines.length) {
        console.error("Invalid medicine index:", index);
        return;
      }

      const currentMedicine = updatedMedicines[index];
      if (!currentMedicine || !currentMedicine.instructions) {
        console.error("Invalid medicine structure at index:", index);
        return;
      }

      if (field === "name") {
        updatedMedicines[index] = {
          ...currentMedicine,
          name: value,
          instructions: {
            ...currentMedicine.instructions,
            name: value,
          },
        };
      } else {
        // Ensure all required instruction fields are preserved
        updatedMedicines[index] = {
          ...currentMedicine,
          instructions: {
            ...currentMedicine.instructions,
            [field]: value,
          },
        };
      }
      setMedicines(updatedMedicines);
    } catch (error) {
      console.error("Error updating medicine:", error);
    }
  };

  const removeMedicine = (index: number) => {
    setMedicines(medicines.filter((_, i) => i !== index));
  };

  const addMedicine = () => {
    setMedicines([
      ...medicines,
      {
        id: Date.now() + Math.random(),
        medicineId: 0,
        name: "",
        instructions: {
          name: "",
          form: "tablet",
          morning: 0,
          afternoon: 0,
          evening: 0,
          night: 0,
          meal: "before_meal",
          note: "",
          molecule: "",
          quantity: 0,
          dosage: "",
          frequency: "daily",
          duration: "",
          durationPeriod: "days",
          unit: "",
        },
      },
    ]);
  };

  const addSymptom = () => {
    if (newSymptom.trim()) {
      setSymptoms([...symptoms, newSymptom.trim()]);
      setNewSymptom("");
    }
  };

  const removeSymptom = (index: number) => {
    setSymptoms(symptoms.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Validate phone before saving
    if (!patient.phone || patient.phone === "") {
      setPhoneError("Phone number is required!");
      return;
    }
    if (!validatePhone(patient.phone)) {
      setPhoneError("Phone number should be 10 digits");
      return;
    }

    onSave({
      patient,
      medicines,
      symptoms,
      finalDiagnosis,
      chiefComplaint,
      advice,
    });
  };

  return (
    <div className="p-4 space-y-6 max-h-[80vh] overflow-y-auto">
      {/* Patient Information Section */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Patient Information
        </h3>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <div className="block">
            <label className="block text-sm font-medium text-gray-700">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
              value={patient.firstName}
              onChange={(e) => updatePatientField("firstName", e.target.value)}
            />
          </div>
          <div className="block">
            <label className="block text-sm font-medium text-gray-700">
              Middle Name
            </label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
              value={patient.middleName || ""}
              onChange={(e) => updatePatientField("middleName", e.target.value)}
            />
          </div>
          <div className="block">
            <label className="block text-sm font-medium text-gray-700">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
              value={patient.lastName}
              onChange={(e) => updatePatientField("lastName", e.target.value)}
            />
          </div>
          <div className="block">
            <label className="block text-sm font-medium text-gray-700">
              Age
            </label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
              value={patient.age || ""}
              onChange={(e) => updatePatientField("age", e.target.value)}
            />
          </div>
          <div className="block">
            <label className="block text-sm font-medium text-gray-700">
              Gender
            </label>
            <div className="mt-1 space-x-4 sm:flex sm:items-center">
              {genders.map((gender) => (
                <div key={gender.id} className="flex items-center">
                  <input
                    id={gender.id}
                    name="gender"
                    value={gender.id}
                    type="radio"
                    checked={patient.gender === gender.id}
                    className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    onChange={(e) =>
                      updatePatientField("gender", e.target.value)
                    }
                  />
                  <label
                    htmlFor={gender.id}
                    className="block ml-2 text-sm font-medium text-gray-900"
                  >
                    {gender.title}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="block">
            <label className="block text-sm font-medium text-gray-700">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              className={`block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm ${
                phoneError ? "border-red-500" : ""
              }`}
              placeholder="Enter 10 digit phone number"
              name="phone"
              maxLength={10}
              pattern="[0-9]{10}"
              inputMode="numeric"
              value={patient.phone || ""}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow numbers (0-9)
                if (/^[0-9]*$/.test(value)) {
                  updatePatientField("phone", value);
                }
              }}
              onKeyPress={(e) => {
                // Prevent non-numeric characters from being entered
                if (
                  !/[0-9]/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Delete" &&
                  e.key !== "Tab"
                ) {
                  e.preventDefault();
                }
              }}
            />
            {phoneError && (
              <span className="mt-2 text-xs text-red-600">{phoneError}</span>
            )}
          </div>
        </div>
      </div>

      {/* Symptoms Section */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Symptoms</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {symptoms.map((symptom, index) => (
            <span
              key={index}
              className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300"
            >
              {symptom}
              <button
                type="button"
                onClick={() => removeSymptom(index)}
                className="ml-2 text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            value={newSymptom}
            onChange={(e) => setNewSymptom(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addSymptom();
              }
            }}
            placeholder="Add symptom"
          />
          <button
            type="button"
            onClick={addSymptom}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Diagnosis Section */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Diagnosis</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Final Diagnosis
            </label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
              value={finalDiagnosis || ""}
              onChange={(e) => setFinalDiagnosis(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Chief Complaint
            </label>
            <input
              type="text"
              className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
              value={chiefComplaint || ""}
              onChange={(e) => setChiefComplaint(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Medicines Section */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Medicines</h3>
          <button
            type="button"
            onClick={addMedicine}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Add Medicine
          </button>
        </div>
        <div className="space-y-4">
          {medicines.map((medicine, index) => (
            <div
              key={medicine.id || index}
              className="p-4 bg-white rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-900">
                  Medicine {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => removeMedicine(index)}
                  className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                >
                  Remove
                </button>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Medicine Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    value={medicine.instructions.name}
                    onChange={(e) =>
                      updateMedicine(index, "name", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Form
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={medicine.instructions.form}
                    onChange={(value) => updateMedicine(index, "form", value)}
                    options={forms}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Quantity
                  </label>
                  <input
                    type="number"
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    value={medicine.instructions.quantity || 0}
                    onChange={(e) =>
                      updateMedicine(
                        index,
                        "quantity",
                        parseInt(e.target.value) || 0
                      )
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Frequency
                  </label>
                  <Select
                    style={{ width: "100%" }}
                    value={medicine.instructions.frequency}
                    onChange={(value) =>
                      updateMedicine(index, "frequency", value)
                    }
                    options={frequencies}
                  />
                </div>
                <div className="lg:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Dosage
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600">
                        Morning
                      </label>
                      <input
                        type="number"
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                        value={medicine.instructions.morning || ""}
                        onChange={(e) =>
                          updateMedicine(
                            index,
                            "morning",
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value) || 0
                          )
                        }
                        placeholder="Morning"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">
                        Afternoon
                      </label>
                      <input
                        type="number"
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                        value={medicine.instructions.afternoon || ""}
                        onChange={(e) =>
                          updateMedicine(
                            index,
                            "afternoon",
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value) || 0
                          )
                        }
                        placeholder="Afternoon"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">
                        Evening
                      </label>
                      <input
                        type="number"
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                        value={medicine.instructions.evening || ""}
                        onChange={(e) =>
                          updateMedicine(
                            index,
                            "evening",
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value) || 0
                          )
                        }
                        placeholder="Evening"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600">
                        Night
                      </label>
                      <input
                        type="number"
                        className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                        value={medicine.instructions.night || ""}
                        onChange={(e) =>
                          updateMedicine(
                            index,
                            "night",
                            e.target.value === ""
                              ? 0
                              : parseInt(e.target.value) || 0
                          )
                        }
                        placeholder="Night"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Meal
                  </label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {mealTypes.map((item) => (
                      <div key={item.value} className="flex items-center">
                        <input
                          id={item.value}
                          name="meal"
                          value={item.value}
                          type="radio"
                          checked={medicine.instructions.meal === item.value}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                          onChange={(e) =>
                            updateMedicine(index, "meal", item.value)
                          }
                        />
                        <label
                          htmlFor={item.value}
                          className="block ml-2 text-sm font-medium text-gray-900"
                        >
                          {item.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Duration
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                      value={medicine.instructions.duration || ""}
                      onChange={(e) =>
                        updateMedicine(index, "duration", e.target.value)
                      }
                      placeholder="Duration"
                    />
                    <Select
                      style={{ width: 120 }}
                      value={medicine.instructions.durationPeriod}
                      onChange={(value) =>
                        updateMedicine(index, "durationPeriod", value)
                      }
                      options={durations}
                    />
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Note
                  </label>
                  <input
                    type="text"
                    className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                    value={medicine.instructions.note || ""}
                    onChange={(e) =>
                      updateMedicine(index, "note", e.target.value)
                    }
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Advice Section */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Advice</h3>
        <textarea
          rows={4}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          value={advice}
          onChange={(e) => setAdvice(e.target.value)}
          placeholder="Enter advice for the patient"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          {mode === "create" ? "Save Visit" : "Save Prescription"}
        </button>
      </div>
    </div>
  );
};

export default EditablePrescription;
